import { Children, useState } from 'react'
import { cn } from './internal/utils'

const SIZE_PX = { small: 24, default: 32, large: 40 }

const resolveSize = (size) => {
  if (typeof size === 'number') return size
  // antd also accepts a responsive object; fall back to the default step.
  if (typeof size === 'object' && size !== null) return SIZE_PX.default
  return SIZE_PX[size] ?? SIZE_PX.default
}

/**
 * antd Avatar. Falls back from `src` to `icon` to the text children, mirroring antd.
 */
const Avatar = ({
  src,
  srcSet,
  alt = '',
  icon,
  shape = 'circle',
  size = 'default',
  gap,
  draggable,
  onError,
  className,
  style,
  children,
  ...rest
}) => {
  const [failed, setFailed] = useState(false)
  const pixels = resolveSize(size)

  const handleError = (event) => {
    // antd only hides the image when onError does not return false.
    if (onError?.(event) === false) return
    setFailed(true)
  }

  const showImage = src && !failed

  return (
    <span
      className={cn(
        'ui-avatar inline-flex shrink-0 items-center justify-center overflow-hidden bg-[var(--gray-600)] text-[var(--text-inverse)]',
        shape === 'circle' ? 'ui-avatar-circle rounded-full' : 'ui-avatar-square rounded-[var(--radius-sm)]',
        className
      )}
      style={{ width: pixels, height: pixels, fontSize: pixels / 2.4, ...style }}
      {...rest}
    >
      {showImage ? (
        <img
          src={src}
          srcSet={srcSet}
          alt={alt}
          draggable={draggable}
          loading="lazy"
          onError={handleError}
          className="ui-avatar-image h-full w-full object-cover"
        />
      ) : (
        (icon ?? <span className="ui-avatar-string leading-none">{children}</span>)
      )}
    </span>
  )
}

const AvatarGroup = ({ maxCount, maxStyle, size = 'default', className, children, ...rest }) => {
  const items = Children.toArray(children)
  const visible = maxCount ? items.slice(0, maxCount) : items
  const hidden = items.length - visible.length

  return (
    <div className={cn('ui-avatar-group flex items-center', className)} {...rest}>
      {visible.map((child, index) => (
        // eslint-disable-next-line react/no-array-index-key -- positional stack
        <span key={index} className={index === 0 ? '' : '-ml-2'}>
          {child}
        </span>
      ))}
      {hidden > 0 ? (
        <span className="-ml-2">
          <Avatar size={size} style={maxStyle}>{`+${hidden}`}</Avatar>
        </span>
      ) : null}
    </div>
  )
}

Avatar.Group = AvatarGroup

export default Avatar
